/**
 * Navigation Mode Hook
 *
 * Manages navigation mode state: "agent" | "admin"
 *  - Defaults to "agent"
 *  - Admin mode only available if user canSeeAgencyAdmin (plan + role check)
 *  - Persists in localStorage
 *  - Auto-resets to "agent" if user loses admin access (plan downgrade, etc.)
 *  - Uses Context to share state across all components
 *
 * ── Access gate ───────────────────────────────────────────────────────────────
 * Previously gated on canAccessAdmin(user?.role), which returned true for every
 * account owner (all signups are created as agentAdmin). This showed the admin
 * toggle to 100% of customers including Solo-plan users.
 *
 * Now gated on canSeeAgencyAdmin(user) from subscriptionConfig.js, which
 * requires BOTH: not an agentUser sub-agent AND on an agency-tier plan.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useUser } from '../context/UserContext';

const STORAGE_KEY = 'parkpro.navMode';

// Create context for shared nav mode state
const NavModeContext = createContext(null);

/**
 * NavModeProvider — Provides shared navigation mode state to all children.
 * Place in AppChrome so it wraps the entire authenticated layout.
 */
export const NavModeProvider = ({ children }) => {
  const { canSeeAgencyAdmin } = useUser();

  const [navModeState, setNavModeState] = useState(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored === 'admin' ? 'admin' : 'agent';
    }
    return 'agent';
  });

  // Derive effective mode: never show admin if user doesn't have access.
  // Handles plan downgrades without requiring a re-login.
  const navMode = (navModeState === 'admin' && !canSeeAgencyAdmin) ? 'agent' : navModeState;

  // Sync localStorage when user loses admin access
  useEffect(() => {
    if (navModeState === 'admin' && !canSeeAgencyAdmin && typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, 'agent');
    }
  }, [navModeState, canSeeAgencyAdmin]);

  const setNavMode = useCallback((mode) => {
    if (mode === 'admin' && !canSeeAgencyAdmin) return;
    setNavModeState(mode);
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, mode);
    }
  }, [canSeeAgencyAdmin]);

  const value = {
    navMode,
    setNavMode,
    hasAdminAccess: canSeeAgencyAdmin,
    canSwitchToAdmin: canSeeAgencyAdmin,
  };

  return (
    <NavModeContext.Provider value={value}>
      {children}
    </NavModeContext.Provider>
  );
};

/**
 * Hook to access navigation mode state.
 * Must be used within NavModeProvider.
 */
// eslint-disable-next-line react-refresh/only-export-components -- hook and provider live together by design
export const useNavMode = () => {
  const context = useContext(NavModeContext);
  const { canSeeAgencyAdmin } = useUser();

  const [fallbackNavMode] = useState(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored === 'admin' ? 'admin' : 'agent';
    }
    return 'agent';
  });

  if (!context) {
    console.warn('useNavMode used outside NavModeProvider, using fallback state');
    return {
      navMode: fallbackNavMode,
      setNavMode: () => {},
      hasAdminAccess: canSeeAgencyAdmin,
      canSwitchToAdmin: canSeeAgencyAdmin,
    };
  }

  return context;
};
